import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, Plus, ChevronDown } from 'lucide-react';
import { PRODUCT_SUGGESTIONS } from '../config/cisaMaturityModel';
import { PRODUCT_STATUSES } from './CisaProductChip';

/**
 * CisaProductPicker - Inline product selection for a function/maturity level
 *
 * Props:
 *   functionCode - e.g., 'ID_AUTH'
 *   maturityLevel - 'INITIAL' | 'ADVANCED' | 'OPTIMAL' (suggestions for this level)
 *   adoptedProducts - array of { productName, status }
 *   onAdopt - (productName, status) => void
 */
const CisaProductPicker = ({ functionCode, maturityLevel, adoptedProducts = [], onAdopt }) => {
  const [query, setQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('PLANNED');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customProduct, setCustomProduct] = useState('');

  const inputRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Get suggestions for this function and level
  const suggestions = useMemo(() => {
    const forLevel = PRODUCT_SUGGESTIONS[functionCode]?.[maturityLevel] || [];
    // Filter out already adopted products
    const adoptedNames = new Set(adoptedProducts.map(p => p.productName.toLowerCase()));
    return forLevel.filter(name => !adoptedNames.has(name.toLowerCase()));
  }, [functionCode, maturityLevel, adoptedProducts]);

  // Filter suggestions by query
  const filteredSuggestions = useMemo(() => {
    if (!query) return suggestions;
    const q = query.toLowerCase();
    return suggestions.filter(name => name.toLowerCase().includes(q));
  }, [suggestions, query]);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!showStatusDropdown) return;
    const onDoc = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showStatusDropdown]);

  const handleAddProduct = (productName) => {
    onAdopt?.(productName, selectedStatus);
    setQuery('');
    setCustomProduct('');
    setShowCustomInput(false);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customProduct.trim()) {
      handleAddProduct(customProduct.trim());
    }
  };

  const selectedStatusConfig = PRODUCT_STATUSES.find(s => s.key === selectedStatus) || PRODUCT_STATUSES[1];

  return (
    <div className="cisa-product-picker">
      {/* Search / Add row */}
      <div className="cisa-product-picker-controls">
        <div className="cisa-product-picker-search">
          <Search size={14} className="cisa-product-picker-search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="cisa-product-picker-input"
            placeholder="Add product..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Status selector */}
        <div className="cisa-product-picker-status-wrap" ref={statusDropdownRef}>
          <button
            type="button"
            className="cisa-product-picker-status-btn"
            onClick={() => setShowStatusDropdown(o => !o)}
            title="Select default status for new products"
          >
            <span
              className="cisa-product-picker-status-dot"
              style={{ background: selectedStatusConfig.color }}
            />
            <span>{selectedStatusConfig.label}</span>
            <ChevronDown size={12} />
          </button>

          {showStatusDropdown && (
            <div className="cisa-product-picker-status-dropdown">
              {PRODUCT_STATUSES.map(s => (
                <button
                  key={s.key}
                  type="button"
                  className={`cisa-product-picker-status-option ${s.key === selectedStatus ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedStatus(s.key);
                    setShowStatusDropdown(false);
                  }}
                >
                  <span className="cisa-product-picker-status-dot" style={{ background: s.color }} />
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Suggestions list */}
      {filteredSuggestions.length > 0 && (
        <div className="cisa-product-picker-suggestions">
          {filteredSuggestions.map(productName => (
            <button
              key={productName}
              type="button"
              className="cisa-product-picker-suggestion"
              onClick={() => handleAddProduct(productName)}
            >
              <span className="cisa-product-picker-suggestion-name">{productName}</span>
              <span className="cisa-product-picker-suggestion-action">
                <Plus size={12} />
                <span className="cisa-product-picker-suggestion-status" style={{ color: selectedStatusConfig.color }}>
                  {selectedStatusConfig.label}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Custom product input */}
      {(query && filteredSuggestions.length === 0) || showCustomInput ? (
        <form className="cisa-product-picker-custom" onSubmit={handleCustomSubmit}>
          <input
            type="text"
            className="cisa-product-picker-custom-input"
            placeholder="Enter custom product name..."
            value={customProduct || query}
            onChange={(e) => setCustomProduct(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            className="cisa-product-picker-custom-btn"
            disabled={!(customProduct || query).trim()}
          >
            <Plus size={14} />
            Add
          </button>
        </form>
      ) : (
        <button
          type="button"
          className="cisa-product-picker-add-custom"
          onClick={() => setShowCustomInput(true)}
        >
          <Plus size={12} />
          Add custom product
        </button>
      )}
    </div>
  );
};

export default CisaProductPicker;
